import React, { useId, useState } from "react"
import cn from "classnames"
import { type UseFormRegisterReturn } from "react-hook-form"
import ErrorFill from "@/skylticons/error_fill"
import { EyeClosedOutline, EyeOpenOutline } from "@/skylticons"
import Label from "./label"
import { mergeRefs } from "../util/react"

export interface InputProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  groupId?: string
  registerFields?: UseFormRegisterReturn
  label?: string | React.ReactNode
  help?: React.ReactNode
  className?: string
  inputClassName?: string
  error?: string | React.ReactNode
  skeleton?: boolean
}

const ForwardableInput: React.ForwardRefRenderFunction<HTMLInputElement, InputProps> = (
  { label, groupId, children, registerFields, className, inputClassName, error, skeleton, help, ...props },
  ref
) => {
  const [passwordShown, setPasswordShown] = useState(false)
  const togglePasswordVisiblity = () => {
    setPasswordShown(passwordShown ? false : true)
  }

  props.autoCapitalize = props.autoCapitalize || "none"
  props.autoComplete = props.autoComplete || "off"
  props.autoCorrect = props.autoCorrect || "off"
  props.spellCheck = props.spellCheck || "false"

  const defaultId = useId()
  props.id = props.id || defaultId

  return (
    <div className={cn("form-group relative", props.name, { "has-error has-feedback": error }, className)} id={groupId}>
      {label && (
        <Label id={props.id} skeleton={skeleton}>
          {label}
        </Label>
      )}
      {help && <p className="paragraph-2xs mb-1 text-ink-85">{help}</p>}
      <div className={cn("input-wrapper", { skeleton })}>
        <input
          className={cn("form-control data-hj-suppress", inputClassName)}
          {...registerFields}
          {...props}
          ref={mergeRefs(ref, registerFields?.ref)}
          type={passwordShown && props.type === "password" ? "text" : props.type}
        />
        {props.type === "password" && (
          <div className="input-icon input-icon-button right" onClick={togglePasswordVisiblity}>
            {passwordShown ? <EyeOpenOutline /> : <EyeClosedOutline />}
          </div>
        )}
        {error && (
          <div className="input-icon-error">
            <ErrorFill />
          </div>
        )}
      </div>
      {error && <p className="field_error text-error">{error}</p>}
      {children}
    </div>
  )
}

const Input = React.forwardRef(ForwardableInput)

export default Input
